import { useEffect, useState } from 'react';

import { AspectRatio } from '../types/editor';
import { ProgressEvent } from '../types/progress';
import { ProgressHandlers } from './usePostProgress';

const API_URL: string = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const TERMINAL = new Set<string>(['done', 'error']);

/**
 * Subscribe to a (project, aspect) render's progress via SSE. The
 * backend multiplexes per-aspect progress on distinct Redis channels
 * (`editor_progress:<project>:<aspect>`); pass aspect so this hook
 * reads the right one.
 *
 * Bumping `attempt` forces a fresh EventSource (used when superseding
 * an in-flight render of the same aspect).
 */
export default function useRenderProgress(
  projectId: number | null,
  aspect: AspectRatio,
  attempt: number = 0,
  { onDone, onError }: ProgressHandlers = {},
): ProgressEvent | null {
  const [event, setEvent] = useState<ProgressEvent | null>(null);

  useEffect(() => {
    if (!projectId) {
      setEvent(null);
      return undefined;
    }
    const url = `${API_URL}/editor/projects/${projectId}/progress/?aspect=${encodeURIComponent(aspect)}`;
    const es = new EventSource(url, { withCredentials: true });

    es.onmessage = (msg) => {
      let data: ProgressEvent;
      try { data = JSON.parse(msg.data) as ProgressEvent; } catch { return; }
      setEvent(data);
      if (TERMINAL.has(data.phase)) {
        es.close();
        if (data.phase === 'done' && onDone) onDone(data);
        if (data.phase === 'error' && onError) onError(data);
      }
    };

    es.onerror = () => {
      // Browser auto-retries via the server's retry: directive until close().
    };

    return () => es.close();
  }, [projectId, aspect, attempt, onDone, onError]);

  return event;
}
